import React, { useState } from 'react';
import { Input, InputProps, InputOnChangeData } from 'semantic-ui-react';

export interface MaskedInputProps
    extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
    /** The real (unmasked) value. Owned by the parent. */
    value: string;
    /** Semantic UI change handler; receives the real value while focused. */
    onChange: (
        event: React.ChangeEvent<HTMLInputElement>,
        data: InputOnChangeData,
    ) => void;
    /** Character used to mask each digit once the field loses focus. */
    maskChar?: string;
}

/**
 * Text input that hides its contents once focus leaves the field.
 *
 * The real value always lives in the parent's state (via `value` / `onChange`);
 * this component only swaps what is *displayed*:
 * - focused -> the real value, so the user can read/edit it
 * - blurred -> one `maskChar` per character, so sensitive digits aren't left on
 *   screen during shoulder-surfing, screenshots, or live support sessions.
 *
 * Unlike `<Input type="password">`, masking is NOT applied while typing, so the
 * user can verify what they entered before moving on. Submission and validation
 * should keep reading the real value from state, never the masked display.
 */
export const MaskedInput = ({
    value,
    onChange,
    maskChar = '•',
    onFocus,
    onBlur,
    ...rest
}: MaskedInputProps) => {
    const [focused, setFocused] = useState(false);

    const displayValue = focused ? value : maskChar.repeat(value.length);

    return (
        <Input
            {...rest}
            value={displayValue}
            onChange={(event, data) => {
                // Edits only happen while focused (the real value is shown
                // then), so masked dots can never be written back to state.
                if (focused) {
                    onChange(event, data);
                }
            }}
            onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                setFocused(true);
                onFocus?.(event);
            }}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                setFocused(false);
                onBlur?.(event);
            }}
        />
    );
};
